import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import EDTable from '../../../../../../../../../BaseComponents/Table/EDTable';
import EDTableHead from '../../../../../../../../../BaseComponents/Table/Components/EDTableHead';
import EDTableRow from '../../../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EDTableCell from '../../../../../../../../../BaseComponents/Table/Components/EDTableCell';
import EDTableBody from '../../../../../../../../../BaseComponents/Table/Components/EDTableBody';
import {
  IVerifikation,
  IVerifikationKonteringData,
} from '../../Queries/VerifikationQuery/VerifikationQuery';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import EDTooltip from '../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import { KodplanConsumer } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import { getKoddelarNames } from '../../../../../../../../../Model/ModelContext/KodplanUtils';
import EDSortableTable, {
  ISortOrder,
} from '../../../../../../../../../BaseComponents/Table/Components/EDSortableTable';
import EDTableSortLabel from '../../../../../../../../../BaseComponents/Table/Components/EDTableSortLabel';
import { listToString } from '../../../../../../../../../Utils/stringUtils';
import { numberOrStringCompare } from '../../../../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import EDIcon from '../../../../../../../../../BaseComponents/Icon/EDIcon';

interface IProps {
  verifikation: IVerifikation;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      },
      headerContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: '0 1 auto',
        marginRight: '15px',
        paddingTop: '20px',
        paddingBottom: '20px',
      },
      rowContainer: {
        display: 'flex',
        flex: '1 1 100%',
        alignItems: 'flex-start',
        [theme.breakpoints.down('sm')]: {
          display: 'block',
        },
      },
      text: {
        display: 'inline',
      },
      labelValueContainer: {
        marginLeft: '15px',
      },
      nonWrappingText: {
        whiteSpace: 'nowrap',
      },
      rightAlign: {
        textAlign: 'right',
      },
      kplanKodDel: { maxWidth: '100px' },
      kplanFirstKodDel: { maxWidth: '250px' },
      cell: {
        padding: '4px 12px',
      },
      headCell: {
        position: 'sticky',
        top: '0px',
        backgroundColor: theme.palette.common.white,
        zIndex: 120,
      },
      attestanterContainer: {
        display: 'inline-box',
        maxWidth: '800px',
      },
      paper: {
        height: '100%',
        overflowY: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 100%',
      },
      overflowContainer: {
        overflowY: 'auto',
      },
      autoKontContainer: {
        display: 'flex',
        flex: '0 1 auto',
        alignItems: 'center',
      },
      overlayRoot: {
        display: 'inline',
      },
    } as const)
);

const displayCurr = (curr: string | number) =>
  findFormatter('TEDFormatCurr').displayer.display(
    curr,
    true,
    'DisplayZeroAsBlank="Y"'
  );

const getKonteringValue =
  (headlines: string[]) =>
  (a: IVerifikationKonteringData, columnName: string) => {
    if (columnName === 'Debet') {
      return a.DEBET;
    }

    if (columnName === 'Kredit') {
      return a.KREDIT;
    }

    if (columnName === 'Text') {
      return a.TEXT;
    }

    if (columnName === 'Automatkontering') {
      return a._Associations?.Automatkontering?.BENAMNING ?? '';
    }

    if (columnName === 'Periodiserad') {
      return a.PERIODISERAD ?? '';
    }

    if (columnName === 'Period, från') {
      return a.PeriodiseradFranDatum ?? '';
    }

    if (columnName === 'Period, till') {
      return a.PeriodiseradTillDatum ?? '';
    }

    const index = headlines.findIndex(headline => headline === columnName);
    return (a as unknown as Record<string, unknown>)['Konto_del' + (index + 1)];
  };

const compareKontering = (
  a: number | string,
  b: number | string,
  order: ISortOrder
) => {
  return numberOrStringCompare(a, b, order);
};

/**
 * Vyn av en verifkation
 */
const Verifikation: React.FunctionComponent<React.PropsWithChildren<IProps>> = (
  props: IProps
) => {
  const { verifikation } = props;
  const { classes, cx } = useStyles();
  const extendedInfo = verifikation.extendedInfo;
  const showLeverantorsFakturaInfo = Boolean(
    extendedInfo && extendedInfo.leverantor.value
  );
  const hasPeriodRow = props.verifikation.konteringar.some(
    x => x.PERIODISERAD === true
  );
  const hasAntal = props.verifikation.konteringar.some(
    kontering => kontering.ANTAL != null
  );

  const filteredRowsSum = verifikation?.konteringar.reduce(
    (debetAndKreditSum, currentVerRow) => {
      if (currentVerRow.DEBET != 0) {
        return {
          ...debetAndKreditSum,
          sumDebet: (debetAndKreditSum.sumDebet += currentVerRow.DEBET),
        };
      }

      if (currentVerRow.KREDIT != 0) {
        return {
          ...debetAndKreditSum,
          sumKredit: (debetAndKreditSum.sumKredit += currentVerRow.KREDIT),
        };
      }
      return debetAndKreditSum;
    },
    { sumDebet: 0, sumKredit: 0 }
  );

  const kreditDiff =
    parseFloat(verifikation.sumKredit) - filteredRowsSum?.sumKredit;
  const debetDiff =
    parseFloat(verifikation.sumDebet) - filteredRowsSum?.sumDebet;

  const hasFilterRows = kreditDiff !== 0 || debetDiff !== 0;

  return (
    <KodplanConsumer>
      {({ activeKodplan }) => {
        const kodplanKodDelar = getKoddelarNames(activeKodplan);
        let periodHeaderNames: string[] = [];
        const antalColumns: string[] = [];
        const hideColumNames: string[] = ['Periodiserad'];
        const kolumner = kodplanKodDelar.concat(['Debet', 'Kredit', 'Text']);
        if (verifikation.innehallerKonteringskoder) {
          kolumner.push('Konteringskod');
        }
        if (verifikation.innehallerAutomatkonteringar) {
          kolumner.push('Automatkontering');
        }

        if (hasPeriodRow) {
          periodHeaderNames = ['Periodiserad', 'Period, från', 'Period, till'];
          kolumner.push(...periodHeaderNames);
        }

        if (hasAntal) {
          antalColumns.push('Antal', 'Enhet');
          kolumner.push(...antalColumns);
        }

        return (
          <div className={classes.mainContainer}>
            <div
              className={classes.headerContainer}
              style={{
                justifyContent: showLeverantorsFakturaInfo
                  ? 'space-between'
                  : 'flex-end',
              }}
            >
              {showLeverantorsFakturaInfo && extendedInfo && (
                <div>
                  <div className={classes.rowContainer}>
                    <div className={classes.labelValueContainer}>
                      <EDTypography className={cx(classes.text)} bold>
                        {extendedInfo.leverantor.benamning}:{' '}
                      </EDTypography>
                      <EDTypography className={classes.text} variant="body2">
                        {extendedInfo.leverantor.value}
                      </EDTypography>
                    </div>
                    <div className={classes.labelValueContainer}>
                      <EDTypography className={cx(classes.text)} bold>
                        {extendedInfo.fakturaNr.benamning}:{' '}
                      </EDTypography>
                      <EDTypography className={classes.text} variant="body2">
                        {extendedInfo.fakturaNr.value}
                      </EDTypography>
                    </div>
                  </div>
                  <div className={classes.rowContainer}>
                    <div className={classes.labelValueContainer}>
                      <EDTypography
                        className={cx(classes.text, classes.nonWrappingText)}
                        bold
                      >
                        {extendedInfo.forfalloDatum.benamning}:{' '}
                      </EDTypography>
                      <EDTypography
                        className={cx(classes.text, classes.nonWrappingText)}
                        variant="body2"
                      >
                        {extendedInfo.forfalloDatum.value}
                      </EDTypography>
                    </div>
                    <div
                      className={cx(
                        classes.labelValueContainer,
                        classes.attestanterContainer
                      )}
                    >
                      <EDTypography
                        className={cx(classes.text, classes.nonWrappingText)}
                        bold
                      >
                        {extendedInfo.attesteradAv.benamning}:{' '}
                      </EDTypography>
                      <EDTypography
                        className={cx(classes.text)}
                        variant="body2"
                      >
                        {listToString(extendedInfo.attesteradAv?.value)}
                      </EDTypography>
                    </div>
                  </div>
                </div>
              )}

              {hasFilterRows && (
                <div>
                  <div className={classes.rowContainer}>
                    <div className={classes.labelValueContainer}>
                      <EDTypography
                        className={cx(classes.text, classes.nonWrappingText)}
                        bold
                      >
                        Verifikationsraderna är filtrerade på din kodbehörighet
                      </EDTypography>
                    </div>
                  </div>
                </div>
              )}

              {verifikation.verifikationsText !== '' && (
                <div>
                  <div className={classes.rowContainer}>
                    <div className={classes.labelValueContainer}>
                      <EDTypography
                        className={cx(classes.text, classes.nonWrappingText)}
                        bold
                      >
                        {'Verifikationstext: '}
                      </EDTypography>
                      <EDTypography
                        className={cx(classes.text)}
                        variant="body2"
                      >
                        {verifikation.verifikationsText}
                      </EDTypography>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div key={'konteringstable'} className={classes.overflowContainer}>
              {/* <AttributGrupp> */}
              <EDSortableTable
                sortInfo={{
                  rows: props.verifikation.konteringar,
                  properties: [],
                }}
                getValue={getKonteringValue(kolumner)}
                compareFunction={compareKontering}
              >
                {({ sortInfo, createSortHandler }) => {
                  let totalDebet = 0;
                  let totalKredit = 0;

                  return (
                    <EDTable autoWidth>
                      <EDTableHead>
                        <EDTableRow>
                          {kolumner.map(header => {
                            const sortProperty = sortInfo.properties.find(
                              x => x.orderBy === header
                            );
                            const sortIndex =
                              sortInfo.properties.length > 1 && sortProperty
                                ? sortInfo.properties.indexOf(sortProperty) + 1
                                : undefined;
                            const columnHeaderName = hideColumNames.includes(
                              header
                            )
                              ? ' '
                              : header;
                            return (
                              <EDTableCell
                                key={header}
                                className={cx(classes.cell, classes.headCell, {
                                  [classes.rightAlign]:
                                    header === 'Debet' || header === 'Kredit',
                                })}
                                onClick={createSortHandler(header, header)}
                              >
                                <EDTableSortLabel
                                  active={Boolean(sortProperty)}
                                  direction={
                                    sortProperty
                                      ? sortProperty.order
                                      : undefined
                                  }
                                  sortIndex={
                                    sortIndex === 0 ? undefined : sortIndex
                                  }
                                  classes={{
                                    overlayRoot: classes.overlayRoot,
                                  }}
                                >
                                  <EDTypography noWrap>
                                    {columnHeaderName}
                                  </EDTypography>
                                </EDTableSortLabel>
                              </EDTableCell>
                            );
                          })}
                        </EDTableRow>
                      </EDTableHead>
                      <EDTableBody>
                        {sortInfo.rows.map((kontering, i) => {
                          totalDebet = totalDebet + parseFloat(kontering.DEBET);
                          totalKredit =
                            totalKredit + parseFloat(kontering.KREDIT);

                          return (
                            <EDTableRow key={i}>
                              {kodplanKodDelar.map((_, j) => {
                                const konteringRecord = kontering as unknown as Record<string, unknown>;
                                return (
                                <EDTableCell key={j} className={classes.cell}>
                                  {(konteringRecord['Kontoben' + (j + 1)] as string) && (
                                    <>
                                      <EDTypography noWrap bold>
                                        {konteringRecord['Konto_del' + (j + 1)] as string}
                                      </EDTypography>
                                      <EDTooltip
                                        title={konteringRecord['Kontoben' + (j + 1)] as string}
                                        placement="bottom"
                                      >
                                        <EDTypography
                                          noWrap
                                          variant="body2"
                                          className={cx(classes.kplanKodDel, {
                                            [classes.kplanFirstKodDel]: j === 0,
                                          })}
                                        >
                                          {konteringRecord['Kontoben' + (j + 1)] as string}
                                        </EDTypography>
                                      </EDTooltip>
                                    </>
                                  )}
                                </EDTableCell>
                              );
                              })}
                              <EDTableCell
                                align="right"
                                className={classes.cell}
                              >
                                <EDTypography
                                  noWrap
                                  className={classes.rightAlign}
                                >
                                  {displayCurr(kontering.DEBET)}
                                </EDTypography>
                              </EDTableCell>
                              <EDTableCell
                                align="right"
                                className={classes.cell}
                              >
                                <EDTypography
                                  noWrap
                                  className={classes.rightAlign}
                                >
                                  {displayCurr(kontering.KREDIT)}
                                </EDTypography>
                              </EDTableCell>
                              <EDTableCell>
                                <EDTypography noWrap className={classes.cell}>
                                  {kontering.TEXT === null
                                    ? ''
                                    : kontering.TEXT}
                                </EDTypography>
                              </EDTableCell>
                              {verifikation.innehallerKonteringskoder && (
                                <EDTableCell className={classes.cell}>
                                  <EDTypography noWrap>
                                    {kontering._Associations?.Konteringskod
                                      ?.KodBenamning == null
                                      ? ''
                                      : kontering._Associations?.Konteringskod
                                          ?.KodBenamning}
                                  </EDTypography>{' '}
                                </EDTableCell>
                              )}
                              {verifikation.innehallerAutomatkonteringar && (
                                <EDTooltip
                                  title={
                                    kontering._Associations?.Automatkontering
                                      ?.BENAMNING == null
                                      ? ''
                                      : 'Raden skapades via automatkontering'
                                  }
                                >
                                  <EDTableCell className={classes.cell}>
                                    <span className={classes.autoKontContainer}>
                                      {kontering._Associations?.Automatkontering
                                        ?.BENAMNING != null && (
                                        <EDIcon
                                          paddingRight={1}
                                          iconType="process_automatic_step"
                                        />
                                      )}
                                      <EDTypography noWrap>
                                        {kontering._Associations
                                          ?.Automatkontering?.BENAMNING == null
                                          ? ''
                                          : kontering._Associations
                                              ?.Automatkontering?.BENAMNING}
                                      </EDTypography>
                                    </span>
                                  </EDTableCell>
                                </EDTooltip>
                              )}
                              {kontering && (
                                <>
                                  {!kontering.PERIODISERAD && hasPeriodRow && (
                                    <EDTableCell />
                                  )}
                                  {kontering.PERIODISERAD && (
                                    <EDTableCell className={classes.cell}>
                                      <EDTooltip
                                        title="Periodiserad"
                                        placement="bottom"
                                      >
                                        <span>
                                          <EDIcon
                                            iconType="record_periodical"
                                            color="secondary"
                                          />
                                        </span>
                                      </EDTooltip>
                                    </EDTableCell>
                                  )}
                                </>
                              )}
                              {hasPeriodRow && (
                                <>
                                  <EDTableCell className={classes.cell}>
                                    <EDTypography>
                                      {kontering.PERIODISERAD
                                        ? kontering.PeriodiseradFranDatum
                                        : ''}
                                    </EDTypography>
                                  </EDTableCell>
                                  <EDTableCell className={classes.cell}>
                                    <EDTypography>
                                      {kontering.PERIODISERAD
                                        ? kontering.PeriodiseradTillDatum
                                        : ''}
                                    </EDTypography>
                                  </EDTableCell>
                                </>
                              )}
                              {hasAntal && (
                                <>
                                  <EDTableCell className={classes.cell}>
                                    <EDTypography>
                                      {kontering.ANTAL}
                                    </EDTypography>
                                  </EDTableCell>
                                  <EDTableCell className={classes.cell}>
                                    <EDTypography>
                                      {kontering._Associations?.Enhet?.ENHET}
                                    </EDTypography>
                                  </EDTableCell>
                                </>
                              )}
                            </EDTableRow>
                          );
                        })}

                        {debetDiff !== 0 && (
                          <EDTableRow key="fillDebet">
                            {kodplanKodDelar.map((item, i) => (
                              <EDTableCell key={i} />
                            ))}
                            <EDTooltip title="Verifikationsraderna är filtrerade på din kodbehörighet">
                              <EDTableCell
                                align="right"
                                className={classes.cell}
                              >
                                <EDTypography
                                  noWrap
                                  className={classes.rightAlign}
                                  italic
                                >
                                  {displayCurr(debetDiff)}
                                </EDTypography>
                              </EDTableCell>
                            </EDTooltip>
                            <EDTableCell />
                            <EDTooltip title="Verifikationsraderna är filtrerade på din kodbehörighet">
                              <EDTableCell>
                                <EDTypography
                                  noWrap
                                  className={classes.rightAlign}
                                  italic
                                >
                                  Summa av debet på de rader som har filtreras
                                  bort
                                </EDTypography>
                              </EDTableCell>
                            </EDTooltip>
                          </EDTableRow>
                        )}

                        {kreditDiff !== 0 && (
                          <EDTableRow key="fillKredit">
                            {kodplanKodDelar.map((item, i) => (
                              <EDTableCell key={i} />
                            ))}
                            <EDTableCell />
                            <EDTooltip title="Verifikationsraderna är filtrerade på din kodbehörighet">
                              <EDTableCell
                                align="right"
                                className={classes.cell}
                              >
                                <EDTypography
                                  noWrap
                                  className={classes.rightAlign}
                                  italic
                                >
                                  {displayCurr(kreditDiff)}
                                </EDTypography>
                              </EDTableCell>
                            </EDTooltip>
                            <EDTooltip title="Verifikationsraderna är filtrerade på din kodbehörighet">
                              <EDTableCell>
                                <EDTypography
                                  noWrap
                                  className={classes.rightAlign}
                                  italic
                                >
                                  Summa av kredit på de rader som har filtreras
                                  bort
                                </EDTypography>
                              </EDTableCell>
                            </EDTooltip>
                          </EDTableRow>
                        )}

                        <EDTableRow key="summarad">
                          {kodplanKodDelar.map((item, i) => (
                            <EDTableCell key={i} />
                          ))}

                          <EDTableCell align="right" className={classes.cell}>
                            <EDTypography
                              noWrap
                              className={classes.rightAlign}
                              bold
                            >
                              {displayCurr(totalDebet + debetDiff)}
                            </EDTypography>
                          </EDTableCell>
                          <EDTableCell align="right" className={classes.cell}>
                            <EDTypography
                              noWrap
                              className={classes.rightAlign}
                              bold
                            >
                              {displayCurr(totalKredit + kreditDiff)}
                            </EDTypography>
                          </EDTableCell>
                          <EDTableCell />
                          {verifikation.innehallerKonteringskoder && (
                            <EDTableCell key="konteringskod"></EDTableCell>
                          )}
                          {hasPeriodRow &&
                            periodHeaderNames.map((name: string) => {
                              return <EDTableCell key={name} />;
                            })}
                          {hasAntal &&
                            antalColumns.map((name: string) => {
                              return <EDTableCell key={name} />;
                            })}
                          {verifikation.innehallerAutomatkonteringar && (
                            <EDTableCell key="konteringar" />
                          )}
                        </EDTableRow>
                      </EDTableBody>
                    </EDTable>
                  );
                }}
              </EDSortableTable>
            </div>
          </div>
        );
      }}
    </KodplanConsumer>
  );
};

export default Verifikation;
